/**
 * Tarifs de l'organisation, en onglets : Échelle d'âges, Droits d'accès (avec l'aperçu du prix d'un groupe),
 * Location d'équipement, Réglages (taxes, où et comment payer). Plan : docs/todo/tarifs-acces-location.md.
 * L'onglet ouvert est dans l'adresse (?onglet=).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { fetchTariffs, type Tariffs } from '../api'
import { AccessTab } from '../components/tarifs/AccessTab'
import { RentalTab } from '../components/tarifs/RentalTab'
import { ScaleTab } from '../components/tarifs/ScaleTab'
import { SettingsTab } from '../components/tarifs/SettingsTab'
import { useOrg } from '../utils/orgContext'

const TABS = ['echelle', 'acces', 'location', 'reglages'] as const
type Tab = (typeof TABS)[number]

export function TarifsPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const tab: Tab = TABS.find((x) => x === params.get('onglet')) ?? 'echelle'
  const { data, isLoading, error } = useQuery({ queryKey: ['tariffs', orgId], queryFn: ({ signal }) => fetchTariffs(orgId, signal) })
  /** Chaque enregistrement renvoie tout l'état des tarifs : on remplace la copie en cache. */
  const update = (next: Tariffs) => queryClient.setQueryData(['tariffs', orgId], next)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tariffs', orgId] })

  return (
    <div className="g-page g-page--wide">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('tarifs.titre')}</h1>
          <p className="g-muted">{t('tarifs.intro')}</p>
        </div>
      </div>
      <div className="g-tabs" role="tablist">
        {TABS.map((x) => (
          <button key={x} type="button" role="tab" aria-selected={tab === x} className={`g-tab${tab === x ? ' g-tab--active' : ''}`} onClick={() => setParams(x === 'echelle' ? {} : { onglet: x }, { replace: true })}>
            {t(`tarifs.onglet_${x}`)}
          </button>
        ))}
      </div>
      {error && <p className="g-alert g-alert--error">{(error as Error).message}</p>}
      {isLoading || !data ? (
        <p className="g-muted">{t('commun.chargement')}</p>
      ) : (
        <section className="g-card g-tarifs">
          {data.settings.prices_include_tax ? (
            <p className="g-hint">{t('tarifs.saisie_ttc', { taux: (Number(data.tax_rate) * 100).toFixed(3).replace(/\.?0+$/, '') })}</p>
          ) : (
            <p className="g-hint">{t('tarifs.saisie_ht')}</p>
          )}
          {tab === 'echelle' && <ScaleTab orgId={orgId} data={data} onSaved={update} onListChanged={refresh} />}
          {tab === 'acces' && <AccessTab orgId={orgId} data={data} onSaved={update} onListChanged={refresh} />}
          {tab === 'location' && <RentalTab orgId={orgId} data={data} onSaved={update} onListChanged={refresh} />}
          {tab === 'reglages' && <SettingsTab orgId={orgId} data={data} onSaved={update} />}
        </section>
      )}
    </div>
  )
}
